import { noop } from 'lodash-es';
import React from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

export enum Option {
  Ai = 'ai',
  Manual = 'manual',
  Template = 'template',
}

type Props = {
  onSelect?: (option: Option) => void;
};

const CARD_WIDTH = '500px';

export const FirstStep = ({ onSelect }: Props) => {
  const intl = useIntl();
  const isPlaybookTemplatesEnabled = useFlag(FlagType.PlaybookTemplates);

  const aiCard = (
    <Card
      borderColor="border"
      mode="bordered"
      size="m"
      width={CARD_WIDTH}
      role="button"
      onClick={noop}
    >
      <Layout
        direction="column"
        p="6"
        alignItems="center"
        justify="center"
        spacing={6}
      >
        <Box styles={{ alignSelf: 'center' }}>
          <Icon color="status.inactive" icon="automation" size="l" />
        </Box>

        <Layout direction="column" spacing={2}>
          <Text variant="subtitle" fontWeight="600" textAlign="center">
            {intl.formatMessage({
              id: 'FirstStep.createGuidelinesAiTitle',
              defaultMessage: 'Create Guidelines with AI',
              description: 'Option title for creating guidelines using AI',
            })}
          </Text>
          <Text variant="body-medium" color="text.secondary" textAlign="center">
            {intl.formatMessage({
              id: 'FirstStep.createGuidelinesAiDescription',
              defaultMessage:
                'Upload a docx, xls, or pdf document and get help from AI to create a playbook.',
              description:
                'Description for the AI-assisted guideline creation option',
            })}
          </Text>
          <Layout justify="center" align="center" mt={2}>
            <Chip
              text={intl.formatMessage({
                id: 'FirstStep.comingSoonLabel',
                defaultMessage: 'Coming Soon',
                description:
                  'Label indicating that the AI-assisted guideline creation option is coming soon',
              })}
            />
          </Layout>
        </Layout>
      </Layout>
    </Card>
  );

  if (isPlaybookTemplatesEnabled) {
    return (
      <Layout justify="center" align="center" flex="1">
        <Layout direction="column" spacing={6} w="1050px">
          <Layout direction="column" spacing={2}>
            <Text variant="title">
              {intl.formatMessage({
                id: 'FirstStep.chooseOptionTitle',
                defaultMessage: 'Choose Option',
                description:
                  'Title for choosing between options in the first step of playbook setup',
              })}
            </Text>
            <Text>
              {intl.formatMessage({
                id: 'FirstStep.chooseOptionDescriptionWithTemplates',
                defaultMessage: 'Choose between the two options to proceed.',
                description:
                  'Description text for choosing between options in the first step of playbook setup when templates are enabled',
              })}
            </Text>
          </Layout>

          <Layout justify="center" spacing={4}>
            {aiCard}

            <Card
              borderColor="status.active"
              mode="bordered"
              size="m"
              width={CARD_WIDTH}
              role="button"
              onClick={() => onSelect?.(Option.Template)}
            >
              <Layout
                direction="column"
                p="6"
                alignItems="center"
                justify="center"
                spacing={6}
              >
                <Box styles={{ alignSelf: 'center' }}>
                  <Icon color="status.active" icon="file" size="l" />
                </Box>

                <Layout direction="column" spacing={2}>
                  <Text variant="subtitle" fontWeight="600" textAlign="center">
                    {intl.formatMessage({
                      id: 'FirstStep.createFromTemplateTitle',
                      defaultMessage: 'Create with a Template',
                      description:
                        'Option title for creating a playbook from a template',
                    })}
                  </Text>
                  <Text
                    variant="body-medium"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {intl.formatMessage({
                      id: 'FirstStep.createFromTemplateDescription',
                      defaultMessage:
                        'Select a pre-configured playbook template to start from',
                      description:
                        'Description for the template-based playbook creation option',
                    })}
                  </Text>
                </Layout>
              </Layout>
            </Card>
          </Layout>

          <Layout justify="center">
            <Button
              variant="action"
              text={intl.formatMessage({
                id: 'FirstStep.createManuallyLabel',
                defaultMessage: 'Or Input Guidelines Manually',
                description:
                  'Link to create a playbook manually without a template or AI',
              })}
              onClick={() => onSelect?.(Option.Manual)}
            />
          </Layout>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout justify="center" align="center" flex="1">
      <Layout direction="column" spacing={6} w="1050px">
        <Layout direction="column" spacing={2}>
          <Text variant="title">
            {intl.formatMessage({
              id: 'FirstStep.chooseOptionTitle',
              defaultMessage: 'Choose Option',
              description:
                'Title for choosing between two options in the first step of playbook setup',
            })}
          </Text>
          <Text>
            {intl.formatMessage({
              id: 'FirstStep.chooseOptionDescription',
              defaultMessage:
                'Choose an option to begin building your playbook.',
              description:
                'Description text for choosing between options in the first step of playbook setup',
            })}
          </Text>
        </Layout>

        <Layout justify="center" spacing={4}>
          {aiCard}

          <Card
            borderColor="status.active"
            mode="bordered"
            size="m"
            width={CARD_WIDTH}
            role="button"
            onClick={() => onSelect?.(Option.Manual)}
          >
            <Layout
              direction="column"
              p="6"
              alignItems="center"
              justify="center"
              spacing={6}
            >
              <Box styles={{ alignSelf: 'center' }}>
                <Icon color="status.active" icon="edit" size="l" />
              </Box>

              <Layout direction="column" spacing={2}>
                <Text variant="subtitle" fontWeight="600" textAlign="center">
                  {intl.formatMessage({
                    id: 'FirstStep.inputGuidelinesManuallyTitle',
                    defaultMessage: 'Input Guidelines Manually',
                    description:
                      'Option title for manually inputting guidelines',
                  })}
                </Text>
                <Text
                  variant="body-medium"
                  color="text.secondary"
                  textAlign="center"
                >
                  {intl.formatMessage({
                    id: 'FirstStep.inputGuidelinesManuallyDescription',
                    defaultMessage:
                      'Input guidelines and sample wording for the AI to run against.',
                    description:
                      'Description for the manual guideline input option',
                  })}
                </Text>
              </Layout>
            </Layout>
          </Card>
        </Layout>
      </Layout>
    </Layout>
  );
};
